import _ from 'lodash';

import { DAY } from 'Utils/time';


/* @ngInject */
export default function EventsPanelService(
  $q,
  $log,
  growl,
  DashboardService,
  EquipmentInfoService,
  EventInfoService,
  appConfig,
) {
  const service = this;

  // move this to a config for the panel
  const ignoredEvents = EventInfoService.eventsConfig.eventPanelExclusions;


  service.eventsModel = {};
  service.getModel = getModel;
  service.getEvents = getEvents;
  service.getFilter = getFilter;
  service.setFilter = setFilter;
  service.hasFilters = hasFilters;
  service.clearFilter = clearFilter;
  service.setSettings = setSettings;
  service.getSettings = getSettings;
  service.handleNewEvent = handleNewEvent;
  service.updateExistingEvent = updateExistingEvent;
  service.removePanelModel = removePanelModel;
  service.flagThisEvent = flagThisEvent;
  service.acknowledgeThisEvent = acknowledgeThisEvent;
  service.deleteThisEvent = deleteThisEvent;
  service.eventDeleted = eventDeleted;
  service.eventHasCoordinates = eventHasCoordinates;
  service.getLat = getLat;
  service.getLong = getLong;
  service.normalizeSettings = normalizeSettings;
  service.getSettingsFromConfig = getSettingsFromConfig;

  const shouldIgnoreEvent = (event = {}) => _.includes(ignoredEvents, event.eventName);

  const HOUR = 60 * 60 * 1000;
  const DEFAULT_ORDER_MODE = 'eventDate';
  const ORDER_MODES = new Set([DEFAULT_ORDER_MODE, 'updatedAt']);
  const DEFAULT_SETTINGS = {
    showAcknowledgedStats: false,
    ordering: {
      mode: DEFAULT_ORDER_MODE,
      staleHoursFallback: undefined,
    },
  };

  /*
   * Reasoning for keeping a separate counter of paged events per model instead of using the events array length:
   *
   * Due to the nature of the events panel retrieving events from the API and also receiving them live through sockets,
   * the number of events in the model's array can change independently of how many events have been retrieved
   * through pagination.
   * This can lead to discrepancies when calculating the `skip` value for subsequent API requests, potentially causing
   * gaps or overlaps in the event history displayed to the user.
   *
   * Here is a scenario illustrating the issue:
   * 1. Initial load – getEvents runs with skip = 0, limit = 30.
   *    We pull A1…A30 from the API, insert them, and set pagedCount(model) = 30 (30 rows have been delivered
   *    through pagination). model.events.length is also 30 for the moment.
   *
   * 2. Socket flood while the user scrolls
   *    - Two brand-new items (B1, B2) arrive and slot ahead of A1.
   *    - Four more (B3…B6) have timestamps interleaved between the A-series and land in the middle.
   *    - Four older entries (B7…B10) belong near the bottom.
   *    After these ten socket inserts, the array holds 40 elements, but pagedCount(model) remains 30 because those
   *    ten never came from the API; they were live pushes.
   * 3. User reaches the bottom – We request older history again with the same page size (limit = 30).
   *    Crucially, we compute skip = pagedCount(model) = 30.
   *    - The backend sorts its full dataset, skips the first 30 newest rows (which now include B1…B6 plus
   *      whichever A’s still live near the top), and returns the 30 rows immediately after that cut.
   *    - Those 30 may include some of the socket events (e.g., B7…B10) and genuinely new historical rows (C1…C20).
   *    We merge any duplicates and insert the rest. After processing, we bump pagedCount(model) to 60, reflecting that
   *    we’ve now consumed 60 rows through pagination — even though model.events.length might differ because sockets
   *    and deletes mutate the array independently.
   * 4. Why not replace pagedCount with events.length?
   *    If we had switched, the second request would send skip = model.events.length = 40.
   *    The API would drop the 40 newest rows (B1…B10 and A1…A30) before responding, so the slice that should have
   *    included C1…C10 simply never arrives.
   *    Every subsequent fetch stays offset by ten, permanently hiding that portion of history.
   *
   *  With a fixed page size (limit = 30 every time), the only safe skip value is
   *  “how many rows did pagination hand us so far?” — that’s what pagedCount tracks.
   *  The moment sockets, local deletes, or reordering change the in-memory array, model.events.length diverges from
   *  that count, so relying on length would immediately create holes or duplicates in paginated results.
   */

  const pagedCount = new WeakMap();

  function getSettingsFromConfig(panelConfig = {}) {
    if (!panelConfig) {
      return {};
    }

    const configSettings = {};
    const configMode = panelConfig.defaultOrderingMode;
    if (ORDER_MODES.has(configMode)) {
      _.set(configSettings, 'ordering.mode', configMode);
    }

    const fallbackRaw = Number(panelConfig.defaultOrderingFallbackHours);
    if (Number.isFinite(fallbackRaw) && fallbackRaw > 0) {
      _.set(configSettings, 'ordering.staleHoursFallback', fallbackRaw);
    }

    return configSettings;
  }

  function normalizeSettings(settings) {
    const normalized = _.merge({}, DEFAULT_SETTINGS, settings);
    normalized.ordering.mode = ORDER_MODES.has(normalized.ordering.mode) ?
      normalized.ordering.mode :
      DEFAULT_ORDER_MODE;

    const parsedFallback = Number(normalized.ordering.staleHoursFallback);
    normalized.ordering.staleHoursFallback = Number.isFinite(parsedFallback) && parsedFallback > 0 ?
      parsedFallback :
      undefined;

    return normalized;
  }

  function ensureModelSettings(panelId, maybeSettings) {
    const model = getModel(panelId);
    const stored = _.get(DashboardService.getState(panelId) || {}, 'settings');
    const source = maybeSettings ?? model.settings ?? stored;
    model.settings = normalizeSettings(source);
    return model.settings;
  }

  function parseTimestamp(value) {
    if (!value) {
      return;
    }
    const time = new Date(value).getTime();
    return Number.isFinite(time) ? time : undefined;
  }

  /**
   * Compute the timestamp used for ordering an event. Panels can sort by the
   * original event time (`eventDate`) or by the most recent update (`updatedAt`).
   * When using updates, a fallback threshold lets older events revert to their
   * occurrence time so they no longer pin the top of the list.
   *
   * @param {object} event Event model
   * @param {object} ordering Ordering configuration
   * @returns {number} Timestamp used for ordering
   */
  function getOrderingTimestamp(event = {}, ordering = DEFAULT_SETTINGS.ordering) {
    const eventDate = parseTimestamp(event.eventDate);
    const updatedAt = parseTimestamp(event.updatedAt);

    if (ordering.mode === 'eventDate') {
      return eventDate ?? updatedAt ?? 0;
    }

    const fallbackHours = ordering.staleHoursFallback;
    if (Number.isFinite(fallbackHours) && fallbackHours > 0 && Number.isFinite(eventDate)) {
      const ageHours = (Date.now() - eventDate) / HOUR;
      if (ageHours >= fallbackHours) {
        return eventDate ?? updatedAt ?? 0;
      }
    }

    return updatedAt ?? eventDate ?? 0;
  }

  function insertOrRepositionEvent(panelId, event, { allowInsert = true, settings } = {}) {
    const panelSettings = ensureModelSettings(panelId, settings);
    const ordering = panelSettings.ordering;

    if (shouldIgnoreEvent(event)) {
      return false;
    }

    const { events } = getModel(panelId);
    const existingIndex = _.findIndex(events, { id: event.id });

    if (existingIndex !== -1) {
      const target = events[existingIndex];
      const previousUpdateTime = getUpdateTimestamp(target);
      const incomingUpdateTime = getUpdateTimestamp(event);

      // If the incoming payload is older than what we already rendered, ignore it.
      if (incomingUpdateTime < previousUpdateTime) {
        return true;
      }

      const previousOrderingTimestamp = getOrderingTimestamp(target, ordering);
      _.merge(target, event);
      const nextOrderingTimestamp = getOrderingTimestamp(target, ordering);
      if (nextOrderingTimestamp !== previousOrderingTimestamp) {
        const [movedEvent] = events.splice(existingIndex, 1);
        reorderEvent(events, movedEvent, ordering, nextOrderingTimestamp);
      }
      return true;
    }

    if (!allowInsert) {
      return false;
    }

    insertEvent(events, event, ordering);
    return true;
  }

  function reorderEvent(events, movedEvent, ordering, movedTimestamp) {
    placeEvent(events, movedEvent, ordering, movedTimestamp);
  }

  function insertEvent(events, event, ordering) {
    placeEvent(events, event, ordering);
  }

  /**
   * Place an event into the ordered list, computing the timestamp when needed.
   *
   * @param {object[]} events Panel event list
   * @param {object} event Event to place
   * @param {object} ordering Ordering configuration
   * @param {number} [timestampOverride] Precomputed timestamp to use
   */
  function placeEvent(events, event, ordering, timestampOverride) {
    const timestamp = timestampOverride ?? getOrderingTimestamp(event, ordering);
    const insertIndex = _.findIndex(events, current => getOrderingTimestamp(current, ordering) < timestamp);
    if (insertIndex === -1) {
      events.push(event);
    } else {
      events.splice(insertIndex, 0, event);
    }
  }

  function getUpdateTimestamp(event = {}) {
    const candidates = [event.updatedAt, event.eventDate];
    for (const value of candidates) {
      const parsed = parseTimestamp(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
    return 0;
  }

  function getModel(panelId) {
    if (!service.eventsModel[panelId]) {
      service.eventsModel[panelId] = {
        events: []
      };
    }
    return service.eventsModel[panelId];
  }

  function subDays(date, daysToSubstract) {
    if (_.isDate(date)) {
      date = date.getTime();
    }
    return date - (DAY * daysToSubstract);
  }

  /**
   * Applies supplied filter arguments, or a saved filter if the {panel, etc} has one
   *
   * @param {object} args
   * @param {{equipment: string[], id: string}} args.panel
   * @param {object} [args.filter={}] Filter
   * @param {number} [args.limit] Max events to retrieve
   * @param {boolean} [args.fresh=false] Disable pagination and reset model
   * @param {object} args.settings Panel settings for event display and ordering
   * @returns {Promise<boolean>} More events to retrieve?
   */
  async function getEvents({
    panel,
    filter = {},
    limit = 15,
    fresh,
    settings,
  }) {
    const model = getModel(panel.id);
    if (!pagedCount.has(model)) {
      pagedCount.set(model, 0);
    }
    ensureModelSettings(panel.id, settings);
    // Paginate, if not a fresh request
    const totalPaged = pagedCount.get(model);
    const skip = fresh ? 0 : totalPaged;
    // If not first page load, we'll increase the amount of events to retrieve when scrolling
    // and even more when user is clearly looking for something

    limit = skip ? skip >= 100 ? 50 : 25 : limit;
    // TODO: Maybe add a pseudo event encouraging them to make use of filters at this point
    const { maxQueryDateRange = 90, equipment, eventNames } = filter;
    const startDate = subDays(Date.now(), maxQueryDateRange);
    // Empty filter.equipment == all equipment
    if (_.isEmpty(equipment)) {
      filter = { ...filter, equipment: panel.equipment };
    }

    if (_.isEmpty(eventNames) && panel.config?.availableEvents?.length) {
      filter = { ...filter, eventNames: [...panel.config.availableEvents] };
    }

    const finalFilter = {
      ...filter,
      startDate,
      limit,
      skip
    };

    return EventInfoService
      .getEvents(finalFilter)
      .then((events) => {
        if (fresh) {
          model.events.length = 0;
          pagedCount.set(model, 0);
        }
        _.forEach(events, (eventItem) => {
          const wasInserted = insertOrRepositionEvent(panel.id, eventItem);
          if (wasInserted) {
            pagedCount.set(model, pagedCount.get(model) + 1);
          }
        });
        return events.length === limit;
      })
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error);
      });
  }


  /**
   * @typedef {object} Filter
   * @property {string[]} equipment Equipment
   * @property {string[]} eventNames Events
   * @property {string[]} defaultEvents List of Default Events to listen
   * @property {string[]} availableEvents List of available events to choose from
   * @property {number|string} date Last X days
   */

  /**
   * @param {string | object} filters Literal or ID for filter's owner (e.g.: panel ID)
   * @param {Array<string>} [filterTypes] Filter types to check for
   * @returns {boolean} The filter object contains active filters
   * @throws If no filters are supplied and lookup fails
   */
  function hasFilters(filters, filterTypes) {
    filterTypes = filterTypes || [
      'equipment',
      'eventNames',
      'maxQueryDateRange',
      'hideAcknowledgedEvents',
      'showOnlyFlaggedEvents'
    ];

    if (!_.isObject(filters)) {
      const id = filters;
      filters = getFilter(id);
      // * Filters may still be undefined so usages should be null safe
    }

    return _.some(filterTypes, (type) => {
      const criteria = _.get(filters, type);
      return _.isArray(criteria) ? criteria.length : Boolean(criteria);
    });

  }

  /**
   * @param {string} requestee Identifier
   * @param {Filter} filter ~
   * @param {boolean} [save=false] (Persistence?) Should filter *set* for session or *save* to API
   * @returns {Promise<object>} The passed filter
   * * Use the `clearFilter` method instead for saving empty/default filters*
   */
  async function setFilter(requestee, filter, save) {

    // For convenience of setting one filter property without needing to define them all
    if (filter) {
      filter = {
        ...EventInfoService.getDefaultFilters(),
        ...filter
      };
    }

    /*
        ! An empty filter saved should 'clear' so we'll remove any existing filter instead of assigning it.
        ! This way we can use getFilter in conditionals without false positives from saved empty filter objects.
      */
    filter = hasFilters(filter) ? filter : undefined;
    filter = _.assign(DashboardService.getState(requestee), { filter });
    DashboardService.setState(requestee, filter);
    if (save) {
      await DashboardService.updateLayout();
    }
    return getFilter(requestee);
  }

  /**
   * Just a shorthand method for calling saveFilter with empty args
   *
   * @param {string} requestee Identifier
   * @param {boolean} [save=true] Should clear save to API?
   * @returns {Promise<*>} Resolves once filter is cleared
   * @deprecated No longer useful/important
   */
  function clearFilter(requestee, save = true) {
    return setFilter(requestee, undefined, save);
  }

  /**
   * Retrieve filter from requestee's Dashboard state
   *
   * @param {string} requestee Identifier (probably panel ID)
   * @param {boolean} populate Filter.equipment should be populated?
   * @returns {object | Promise<object>} Filter (STM)
   * If you are populating the filter equipment, you should *await* (not chain) a promise because
   * if your filter turns out to have empty equipment, you will *not* receive a promise.
   */
  function getFilter(requestee, populate) {
    const { filter } = DashboardService.getState(requestee);
    return populate && filter && filter.equipment.length ?
      EquipmentInfoService
        .getEquipmentById(filter.equipment)
        .then(populated => ({ ...filter, equipment: populated })) :
      filter;
  }

  async function setSettings(requestee, settings, save) {
    const normalized = normalizeSettings(settings);
    const state = _.assign({}, DashboardService.getState(requestee));
    state.settings = normalized;
    DashboardService.setState(requestee, state);
    ensureModelSettings(requestee, normalized);
    if (save) {
      await DashboardService.updateLayout();
    }
    return _.cloneDeep(normalized);
  }

  function getSettings(panel) {
    const { id, config } = panel;
    const configuredPanelSettings = getSettingsFromConfig(config);
    const stored = _.get(DashboardService.getState(id) || {}, 'settings');
    const defaultSettingOverrides = _.merge({}, configuredPanelSettings, stored);
    return _.cloneDeep(ensureModelSettings(id, defaultSettingOverrides));
  }

  function handleNewEvent(panel, newEvent, settings) {
    return insertOrRepositionEvent(panel.id, newEvent, { settings });
  }

  function updateExistingEvent(panelId, eventObj, settings) {
    return insertOrRepositionEvent(panelId, eventObj, { allowInsert: false, settings });
  }

  function eventDeleted(panelId, eventId) {
    const events = service.eventsModel[panelId]?.events;
    if (!events?.length) {
      return false;
    }

    const index = _.findIndex(events, { id: eventId });
    if (index === -1) {
      return false;
    }

    events.splice(index, 1);
    return true;
  }

  function removePanelModel(panelId) {
    if (service.eventsModel[panelId]) {
      delete service.eventsModel[panelId];
    }
  }

  function flagThisEvent(event) {
    const FLAG_BACKUP = event.flagged;
    event.flagged = !event.flagged;

    return EventInfoService.updateEvent(event.id, {
      flagged: event.flagged
    })
      .catch(() => {
        event.flagged = FLAG_BACKUP;
      });
  }

  function acknowledgeThisEvent(event) {
    if (event.data.eventAcknowledgedTime && event.data.eventAcknowledgedBy) {
      growl.info(`Event was already acknowledged by ${event.data.eventAcknowledgedBy}`, { ttl: 5000 });
      return Promise.resolve(); // If the event was already acknowledged just say so and return nothing
    }
    event.data = {
      ...event.data,
      eventAcknowledgedBy: appConfig.user.name,
      eventAcknowledgedTime: new Date().toISOString(),
    };

    return EventInfoService.updateEvent(event.id, { data: event.data })
      .then(res => res.data[0]); // return the updated event (res.data[0]) back to the caller
  }

  function deleteThisEvent(panelId, event) {
    return EventInfoService
      .deleteEvent(event.id, { deleted: true })
      .then(() => _.remove(service.eventsModel[panelId].events, { id: event.id }));
  }


  function eventHasCoordinates(event) {
    return Boolean(getLat(event, true) && getLong(event, true));

  }

  /*
   * These previously read `return lat || ignoreEquipmentLocation ? lat : fallback`,
   * which parses as `(lat || ignoreEquipmentLocation) ? lat : fallback` and looks
   * like a precedence bug. It is not — that is behaviourally identical to the
   * intended `lat || (ignoreEquipmentLocation ? lat : fallback)` for every input —
   * but written as an early return it can no longer be misread, and clarifying
   * parentheses would trip `no-extra-parens`.
   *
   * `ignoreEquipmentLocation` defaults to true and no caller currently passes
   * false, so the equipment-settings fallback is presently unreachable. Kept
   * because the signature is used across several components.
   *
   * GPS events store coordinates as data.lat/data.long while other equipment uses
   * data.latitude/data.longitude, and either may be a literal or a {value} object
   * — hence the double normalisation. See the consolidation TODO in the API's
   * EventService.findLastLocationEvent.
   */
  function getLat({ data: { latitude = {}, lat }, equipment }, ignoreEquipmentLocation = true) {
    lat = lat || latitude;
    lat = Number(lat?.value || lat);
    if (lat || ignoreEquipmentLocation) {
      return lat;
    }
    return Number(equipment.settings.latitude);
  }

  function getLong({ data: { longitude = {}, long }, equipment }, ignoreEquipmentLocation = true) {
    long = long || longitude;
    long = Number(long?.value || long);
    if (long || ignoreEquipmentLocation) {
      return long;
    }
    return Number(equipment.settings.longitude);
  }

}
